import React, { useCallback, useContext, useMemo } from 'react';
import { Filter } from '@fabric-ui/components';
import { DrillDown } from '@fabric-ui/icons';
import { iconSize } from '@fabric-ui/utils';
import { Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ErrorLoadingWidget, NoDataFoundWidget } from '../../../../components/Widgets/layout.components';
import { ChartClickInteraction } from '../../../../components/Widgets/types';
import { AsyncQueryStatus } from '../../../../hooks/queryObjectAsync/useQueryObjectAsyncWithCachedResults.utils';
import { useFormattingRules } from '../../../FormattingRules/hooks';
import { hasFormattingRuleOnWidget } from '../../../FormattingRules/utils';
import { OTHERS } from '../../constants';
import { CustomDashboardContext } from '../../CustomDashboardContext';
import { shouldRenderWidget } from '../../CustomDashboardsUtils';
import { useGetWidgetData } from '../../hooks/useGetWidgetData';
import { DwQueryRequest, TextSubType, WidgetCategory } from '../../types';
import { AllTypeWidgets } from '../../types/combined.types';
import { useDrillDown } from '../Interactions/useDrillDown';
import Preview from '../widgetsPreviews/Preview';
import {
  getUnapplicableAbacFilterMessage,
  getUnapplicableFilterKeys,
  getUnapplicableFilterState,
  UnapplicableFilterType,
  type UnapplicableFilterState,
} from './unapplicableFilter.utils';
import { WidgetErrorBoundary } from './WidgetErrorBoundry';
import WidgetLoadingState from './WidgetHistoricLoadingState/WidgetLoadingState';

export interface WidgetProps {
  widget: AllTypeWidgets;
  setWidget?: (w) => void;
  globalFilters?: Filter;
  isEdit?: boolean;
  visible?: boolean;
  children: any;
  selected?: any;
  onSelect?: (v) => void;
  clickInteractions?: ChartClickInteraction[];
  enableResponsiveLegend?: boolean;
}

const defaultClickInteractions: ChartClickInteraction[] = [];

const UnapplicableFilterDescription = ({
  state,
  canEditFilters,
  onRemoveFilters,
}: {
  state: UnapplicableFilterState;
  canEditFilters: boolean;
  onRemoveFilters: () => void;
}) => {
  if (state.type === UnapplicableFilterType.Abac) {
    return state.message;
  }
  return (
    <>
      {state.message}
      {canEditFilters && (
        <>
          <br />
          <Button
            className="draggableCancel min-w-0 p-0 font-semibold text-content-interactive-primary-default"
            variant="text"
            onClick={onRemoveFilters}>
            Remove or replace these filters to view the results.
          </Button>
        </>
      )}
    </>
  );
};

const Widget: React.FC<WidgetProps> = ({
  widget,
  setWidget,
  isEdit = false,
  children,
  visible = true,
  globalFilters,
  selected,
  onSelect,
  clickInteractions = defaultClickInteractions,
  enableResponsiveLegend,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { globalInteractiveFilters, isAllowedEdit, newDashboard, setFilters } = useContext(CustomDashboardContext);
  const getFieldDisplayName = useCallback(
    (fieldName: string) => {
      const field = globalInteractiveFilters.find(({ value, otherAliases }) => value === fieldName || otherAliases?.includes(fieldName));
      return field?.title ?? field?.displayName ?? fieldName;
    },
    [globalInteractiveFilters]
  );
  const onWidgetError = useCallback(
    (error: unknown) => {
      const message = getUnapplicableAbacFilterMessage([error], getFieldDisplayName);
      if (message) {
        enqueueSnackbar(message, { variant: 'error', key: message, preventDuplicate: true });
      }
    },
    [enqueueSnackbar, getFieldDisplayName]
  );
  const {
    data: originalData,
    totals: originalTotals,
    totalRowCount,
    isLoading: isLoadingOriginalData,
    errors,
    isAsyncQuery,
    isDebouncing,
    asyncQueryStatus,
  } = useGetWidgetData({ widget, contextFilter: globalFilters, visible, debounceQueries: isEdit, onError: onWidgetError });
  const { isLoading: isLoadingFormattingRule } = useFormattingRules();
  const canEditFilters = isAllowedEdit || !!newDashboard.allowViewersToChangeFilters;
  const unapplicableFilterState = useMemo(() => getUnapplicableFilterState(errors, getFieldDisplayName), [errors, getFieldDisplayName]);
  const unapplicableFilterKeys = useMemo(
    () =>
      getUnapplicableFilterKeys(
        unapplicableFilterState?.type === UnapplicableFilterType.Global ? unapplicableFilterState.fieldNames : [],
        globalInteractiveFilters
      ),
    [unapplicableFilterState, globalInteractiveFilters]
  );
  const removeUnapplicableFilters = () =>
    setFilters(prev => Object.fromEntries(Object.entries(prev).filter(([key]) => !unapplicableFilterKeys.has(key))));

  const { drillDownData, isLoadingDrillDown, totalsDrillDown, activeIndex, onSelectedDrillDown, allDrillDownFields, newQueryObject } =
    useDrillDown({
      widget,
      setWidget,
      contextFilter: globalFilters,
      enable: [WidgetCategory.Bar, WidgetCategory.Pie].includes(widget.category),
    });

  const isCrossTable = widget.category === WidgetCategory.Table && widget.definition.custom.crossTableView;
  const isLoading =
    isLoadingOriginalData ||
    (isLoadingDrillDown && !isCrossTable) ||
    (isAsyncQuery && isDebouncing) ||
    (hasFormattingRuleOnWidget(widget) && isLoadingFormattingRule);
  const isPreview = !shouldRenderWidget({ widget });

  const data = useMemo(
    () => (activeIndex !== 0 && drillDownData && !isCrossTable ? drillDownData : originalData),
    [drillDownData, originalData, activeIndex]
  );

  const responseTotals = useMemo(
    () => (activeIndex !== 0 && totalsDrillDown && !isCrossTable ? totalsDrillDown : originalTotals),
    [totalsDrillDown, originalTotals, activeIndex]
  );

  if (isPreview) {
    return <Preview type={widget.type} />;
  }

  const isLoadingWidget =
    isLoading || (isAsyncQuery && asyncQueryStatus === AsyncQueryStatus.Loading) || asyncQueryStatus === AsyncQueryStatus.Polling;

  const interactions = [
    ...(clickInteractions || []),
    ...(allDrillDownFields.length > 1 && [WidgetCategory.Bar, WidgetCategory.Pie].includes(widget.category)
      ? [
          {
            title: 'Drill Down',
            onClick: args => {
              if ('widgetData' in args || !args?.activeLabel || args?.activeLabel !== OTHERS) {
                return onSelectedDrillDown(args);
              }

              const firstDimension = widget.requests[0].select.dims[0].alias || widget.requests[0].select.dims[0].name;
              return onSelectedDrillDown({
                ...args,
                notOthers: data.filter(obj => obj[firstDimension] !== OTHERS).map(obj => obj[firstDimension]),
              });
            },
            isEnabledForCell: () => isDrillDownEnabledForWidget(widget) && !!allDrillDownFields[activeIndex + 1],
            icon: <DrillDown style={iconSize(18)} />,
          },
        ]
      : []),
  ];

  return (
    <WidgetErrorBoundary context={{ data, globalFilters, isEdit, responseTotals, selected, visible, widget }}>
      {isLoadingWidget ? (
        <WidgetLoadingState isAsync={isAsyncQuery} />
      ) : data.length || widget.type === TextSubType.Text ? (
        <WidgetImpl
          widget={widget}
          activeIndex={activeIndex}
          clickInteractions={interactions}
          data={data}
          globalFilters={globalFilters}
          isLoading={isLoading}
          isEdit={isEdit}
          newQueryObject={newQueryObject}
          onSelect={onSelect}
          responseTotals={responseTotals}
          selected={selected}
          totalRowCount={totalRowCount}
          enableResponsiveLegend={enableResponsiveLegend}>
          {children}
        </WidgetImpl>
      ) : unapplicableFilterState ? (
        <NoDataFoundWidget
          showImage
          description={
            <UnapplicableFilterDescription
              state={unapplicableFilterState}
              canEditFilters={canEditFilters}
              onRemoveFilters={removeUnapplicableFilters}
            />
          }
        />
      ) : (
        <>
          {!!errors?.length && (
            <ErrorLoadingWidget
              text={errors[0]?.extensions?.message?.includes('query timed out') ? 'Oops, Query took too long to load' : ''}
            />
          )}
          {data?.length === 0 && !isLoading && errors?.length === 0 && <NoDataFoundWidget showImage />}
        </>
      )}
    </WidgetErrorBoundary>
  );
};

export const WidgetImpl = (
  props: React.PropsWithChildren<
    Omit<WidgetProps, 'visible' | 'setWidget' | 'children'> & {
      isLoading?: boolean;
      data?: unknown[];
      responseTotals?: unknown;
      activeIndex?: number;
      newQueryObject?: DwQueryRequest;
      totalRowCount?: number;
    }
  >
) => {
  const {
    widget,
    isEdit = false,
    children = null,
    globalFilters,
    selected,
    onSelect,
    clickInteractions = defaultClickInteractions,
    isLoading,
    data = [],
    responseTotals,
    activeIndex = 0,
    newQueryObject,
    totalRowCount,
    enableResponsiveLegend,
  } = props;

  if (!React.isValidElement(children)) {
    return null;
  }

  return React.cloneElement(children as any, {
    ...(responseTotals && typeof responseTotals === 'object' ? { responseTotals } : {}),
    totalRowCount,
    data,
    isLoading,
    isEdit,
    selected,
    onSelect,
    globalFilters,
    widget:
      [WidgetCategory.Bar, WidgetCategory.Pie].includes(widget.category) && activeIndex !== 0
        ? { ...widget, requests: [newQueryObject] }
        : widget,
    clickInteractions,
    enableResponsiveLegend,
  });
};

export default Widget;

const isDrillDownEnabledForWidget = (widget: AllTypeWidgets) => {
  if (widget.category === WidgetCategory.Pie) {
    return widget.requests[0].select.dims.length === 1 && widget.requests[0].select.metrics.length === 1;
  }
  return widget.category === WidgetCategory.Bar;
};
